// src/services/staffConfirmation/requestHash.ts - the staff request hash.
//
// A staff confirmation names exactly one request: 88ID writes the hash into the
// token (`rh`) and the api recomputes it from the request it receives. The hash
// is
//
//   hex(SHA-256(METHOD + "\n" + path + "\n" + JCS(body) + "\n" + batchIds))
//
// - METHOD is upper case.
// - path is the request path with no host, plus "?" and the query when there is
//   one. The query's pairs are kept exactly as encoded and ordered by key
//   (code-unit order, stable, so repeated keys keep their order and the api reads
//   the same values). The office SENDS this ordered form, so the api sorts a query
//   that is already sorted.
// - JCS(body) is the RFC 8785 form of the JSON body, or "" when there is none.
// - batchIds are the ids of every record a bulk request acts on, sorted and
//   joined with ",", or "" when the request acts on one record named in the path.
//
// The shared vectors in contract/staff-request-hash.vectors.json are the
// contract. A change here that moves a vector is a contract change.
import { canonicalJson, CanonicalJsonError } from "./canonicalJson";

export interface StaffRequest {
  method: string;
  /** Path (no host) and optional query, already percent-encoded. */
  path: string;
  /** The canonical body string ("" for none), from canonicalRequestBody. */
  body: string;
  batchIds?: readonly string[];
}

const keyOf = (pair: string): string => {
  const eq = pair.indexOf("=");
  return eq === -1 ? pair : pair.slice(0, eq);
};

/** The query's pairs ordered by key; empty pairs dropped. No leading "?". */
export const canonicalQuery = (query: string): string => {
  const pairs = query
    .replace(/^\?/, "")
    .split("&")
    .filter((pair) => pair.length > 0);
  // Array.prototype.sort is stable (ES2019), so equal keys keep their order.
  return pairs
    .map((pair, index) => ({ pair, index, key: keyOf(pair) }))
    .sort((a, b) =>
      a.key < b.key ? -1 : a.key > b.key ? 1 : a.index - b.index
    )
    .map(({ pair }) => pair)
    .join("&");
};

/** `pathname` plus the canonical query, as the hash and the wire carry it. */
export const canonicalPath = (pathname: string, search: string): string => {
  const query = canonicalQuery(search);
  return query ? `${pathname}?${query}` : pathname;
};

/** A body the office cannot state as JSON is not confirmable. */
export class UnconfirmableBodyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnconfirmableBodyError";
  }
}

const isBinaryBody = (data: unknown): boolean =>
  (typeof FormData !== "undefined" && data instanceof FormData) ||
  (typeof Blob !== "undefined" && data instanceof Blob) ||
  (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams) ||
  data instanceof ArrayBuffer ||
  ArrayBuffer.isView(data);

/**
 * The canonical body for a request's `data`: "" for none, the RFC 8785 form of
 * a JSON value otherwise. A string is parsed first (a re-send carries the body
 * axios already serialised). Multipart, binary and form bodies are refused.
 */
export const canonicalRequestBody = (data: unknown): string => {
  if (data === undefined || data === null || data === "") return "";
  if (isBinaryBody(data))
    throw new UnconfirmableBodyError("Only a JSON body can be confirmed.");
  let value: unknown = data;
  if (typeof data === "string") {
    try {
      value = JSON.parse(data);
    } catch {
      throw new UnconfirmableBodyError("The body is not JSON.");
    }
  }
  try {
    return canonicalJson(value);
  } catch (error) {
    if (error instanceof CanonicalJsonError)
      throw new UnconfirmableBodyError(error.message);
    throw error;
  }
};

/** The exact text that is hashed. */
export const staffRequestHashInput = (request: StaffRequest): string =>
  [
    request.method.toUpperCase(),
    request.path,
    request.body,
    [...(request.batchIds ?? [])].sort().join(","),
  ].join("\n");

const toHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

/** hex(SHA-256(input)) with the browser's Web Crypto (secure contexts only). */
export const sha256Hex = async (input: string): Promise<string> => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error("Web Crypto is not available.");
  const digest = await subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input)
  );
  return toHex(digest);
};

/** The staff request hash for one request. */
export const staffRequestHash = (request: StaffRequest): Promise<string> =>
  sha256Hex(staffRequestHashInput(request));
